import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'sonner'

import { AuthProvider } from '@/contexts/AuthContext'
import { ThemeProvider } from '@/contexts/ThemeContext'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { MainLayout } from '@/components/layout/MainLayout'
import { DrawerProvider, DrawerContainer } from '@/components/ui/drawer'
import { BlogMetadataProvider } from '@/contexts/BlogMetadataContext'

// Pages
import { Login } from '@/pages/Login'
import { ForgotPassword } from '@/pages/ForgotPassword'
import { ResetPassword } from '@/pages/ResetPassword'
import { Dashboard } from '@/pages/Dashboard'
import { Tenants } from '@/pages/Tenants'
import { Hierarchy } from '@/pages/Hierarchy'
import { HierarchyAudit } from '@/pages/HierarchyAudit'
import { Users } from '@/pages/Users'
import { BlogPosts } from '@/pages/BlogPosts'
import { BlogCategories } from '@/pages/BlogCategories'
import { BlogTags } from '@/pages/BlogTags'
import { BlogAnalytics } from '@/pages/BlogAnalytics'
import BlogCalendar from '@/pages/BlogCalendar'
import BlogTemplates from '@/pages/BlogTemplates'
import BlogMediaOptimization from '@/pages/BlogMediaOptimization'
import { BillingSubscriptions } from '@/pages/BillingSubscriptions'
import { BillingInvoices } from '@/pages/BillingInvoices'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <Router>
          <AuthProvider>
            <DrawerProvider>
              <BlogMetadataProvider>
              <Routes>
                {/* Auth Routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* Protected Routes with Layout */}
                <Route
                  element={
                    <ProtectedRoute>
                      <MainLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/tenants" element={<Tenants />} />
                  {/* SUB-3.13 · FR62. Declared before any future /tenants/:id
                      route: react-router would otherwise capture "hierarchy"
                      as an id, which is the same trap the backend route has. */}
                  <Route path="/tenants/hierarchy" element={<Hierarchy />} />
                  <Route path="/tenants/new" element={<div>New Tenant Page (TODO)</div>} />
                  <Route path="/users" element={<Users />} />
                  <Route path="/blog" element={<BlogAnalytics />} />
                  <Route path="/blog/analytics" element={<BlogAnalytics />} />
                  <Route path="/blog/calendar" element={<BlogCalendar />} />
                  <Route path="/blog/posts" element={<BlogPosts />} />
                  <Route path="/blog/categories" element={<BlogCategories />} />
                  <Route path="/blog/tags" element={<BlogTags />} />
                  <Route path="/blog/templates" element={<BlogTemplates />} />
                  <Route path="/blog/media-optimization" element={<BlogMediaOptimization />} />
                  <Route path="/analytics" element={<div>Analytics Page (TODO)</div>} />
                  <Route path="/analytics/revenue" element={<div>Revenue Analytics (TODO)</div>} />
                  <Route path="/analytics/usage" element={<div>Usage Analytics (TODO)</div>} />
                  <Route path="/billing" element={<Navigate to="/billing/subscriptions" replace />} />
                  <Route path="/billing/subscriptions" element={<BillingSubscriptions />} />
                  <Route path="/billing/invoices" element={<BillingInvoices />} />
                  <Route path="/support" element={<div>Support Page (TODO)</div>} />
                  <Route path="/security" element={<div>Security Page (TODO)</div>} />
                  {/* SUB-3.13 · FR59, NFR5. The platform's structural history.
                      NOT an account's own activity log: that is activity_logs
                      and it belongs to the account (FR61, SUB-3.14). */}
                  <Route path="/security/audit" element={<HierarchyAudit />} />
                  <Route path="/security/alerts" element={<div>Security Alerts (TODO)</div>} />
                  <Route path="/settings" element={<div>Settings Page (TODO)</div>} />
                </Route>

                {/* Catch all - redirect to dashboard */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>

              <DrawerContainer />
            </BlogMetadataProvider>

            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: 'hsl(var(--background))',
                  color: 'hsl(var(--foreground))',
                  border: '1px solid hsl(var(--border))',
                },
              }}
            />
            </DrawerProvider>
          </AuthProvider>
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  )
}

export default App